import { adminService } from "@/api/admin/admin.service";
import { TAdminUser, TRoleResponse, TRoleUser } from "@/api/admin/admin.types";
import { USER_ROLES } from "@/api/user/user.types";
import { AddAdminModal } from "@/components/admin/AddAdminModal";
import { UserDetailModal } from "@/components/admin/UserDetailModal";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useStore } from "@/store/store";
import { extractErrorMessage } from "@/utils";
import { maskEmail, maskPhone } from "@/utils/maskUtils";
import { Eye, Loader2, UserMinus } from "lucide-react";
import { useState } from "react";

const ADMIN_ROLE_NAMES = [
  USER_ROLES.admin,
  USER_ROLES.super_admin,
  USER_ROLES.moderator,
  USER_ROLES.user_manager,
];

function formatRoleName(roleName: string): string {
  const roleMap: Record<string, string> = {
    [USER_ROLES.admin]: "Admin",
    [USER_ROLES.super_admin]: "Super Admin",
    [USER_ROLES.moderator]: "Moderator",
    [USER_ROLES.user_manager]: "User Manager",
  };
  return roleMap[roleName] || roleName || "N/A";
}

const PAGE_SIZE = 20;

export function AdminSettingsPage() {
  const [reloadTick, setReloadTick] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; admin: TAdminUser | null }>({
    open: false,
    admin: null,
  });
  const [isRevoking, setIsRevoking] = useState(false);
  const [viewAdminId, setViewAdminId] = useState<string | null>(null);
  // `profile` is the populated current-user slice (`user` is never set on login).
  const currentUser = useStore.use.profile();

  const reload = () => setReloadTick((t) => t + 1);

  // The users list endpoint has no server-side role filter (role comes via the
  // UserRole join), so resolve each admin role name to its role_id via
  // getRoles(), then fetch each role's members and merge + dedupe by user id.
  const fetcher = async (query: TDataGridQuery): Promise<TAdminUser[]> => {
    const signal = query.signal;
    const rolesResponse = await adminService.getRoles({ signal });
    if (rolesResponse.error) {
      throw new Error(await extractErrorMessage(rolesResponse.error));
    }

    const adminRoleNames = ADMIN_ROLE_NAMES as readonly string[];
    const adminRoles = (rolesResponse.data?.data ?? []).filter((role) =>
      adminRoleNames.includes(role.name)
    );

    const results = await Promise.allSettled(
      adminRoles.map(async (role) => {
        const response = await adminService.getRoleUsers(role.role_id, { limit: 100 }, { signal });
        if (response.error) {
          const errorMessage = await extractErrorMessage(response.error);
          console.error(`Error fetching ${role.name} users:`, errorMessage);
          return { role, users: [] as TRoleUser[] };
        }
        return { role, users: response.data?.data?.items ?? [] };
      })
    );

    const byId = new Map<string, TAdminUser>();
    for (const result of results) {
      if (result.status === "rejected") continue;
      const { role, users } = result.value;
      const roleEntry: TRoleResponse = { role_id: role.role_id, name: role.name };
      users.forEach((user) => {
        const existing = byId.get(user.id);
        if (existing) {
          if (!existing.roles?.some((r) => r.name === role.name)) {
            existing.roles = [...(existing.roles ?? []), roleEntry];
          }
          return;
        }
        byId.set(user.id, {
          id: user.id,
          email: user.email ?? "",
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
          role: role.name,
          status_id: user.status_id ?? 0,
          email_verified: false,
          phone_verified: false,
          two_factor_enabled: false,
          created_at: "",
          updated_at: "",
          roles: [roleEntry],
        });
      });
    }

    return [...byId.values()];
  };

  const primaryRole = (admin: TAdminUser): string => {
    const roleName = admin.roles?.[0]?.name ?? admin.role;
    return formatRoleName(roleName ?? "");
  };

  const fullName = (admin: TAdminUser) => `${admin.first_name} ${admin.last_name}`.trim();

  const handleRevokeConfirm = async () => {
    const admin = confirmDialog.admin;
    if (!admin) return;

    // Guard against self-lockout: the last super_admin revoking their own
    // role would permanently lock everyone out of the admin panel.
    if (currentUser && admin.id === currentUser.id) {
      setActionError("You cannot revoke your own admin role.");
      setConfirmDialog({ open: false, admin: null });
      return;
    }

    setActionError(null);
    setIsRevoking(true);

    try {
      const roleToRevoke = admin.roles?.[0]?.name ?? admin.role ?? "admin";
      const response = await adminService.revokeRole(admin.id, roleToRevoke);

      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        setActionError(`Failed to revoke admin role: ${errorMessage}`);
      } else {
        setConfirmDialog({ open: false, admin: null });
        reload();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to revoke admin role";
      setActionError(errorMessage);
    } finally {
      setIsRevoking(false);
    }
  };

  const columns: TColumnDef<TAdminUser>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (a) => fullName(a).toLowerCase(),
      cell: (a) => <span className="font-medium text-foreground">{fullName(a) || "—"}</span>,
    },
    {
      id: "email",
      header: "Email",
      sortable: true,
      accessor: (a) => maskEmail(a.email),
    },
    {
      id: "phone",
      header: "Phone",
      accessor: (a) => (a.phone_number ? maskPhone(a.phone_number) : "N/A"),
    },
    {
      id: "role",
      header: "Role",
      sortable: true,
      accessor: (a) => primaryRole(a),
      cell: (a) => (
        <div className="flex flex-wrap gap-1">
          {(a.roles?.length ? a.roles : [{ role_id: a.id, name: a.role }]).map((r) => (
            <Badge key={r.role_id} variant="secondary">
              {formatRoleName(r.name ?? "")}
            </Badge>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Admin Settings</h1>
          <p className="mt-2 text-sm text-muted-foreground">Here you can manage admin accounts</p>
        </div>

        {/* Action error banner */}
        {actionError && (
          <div
            role="alert"
            className="flex items-center justify-between rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <span>{actionError}</span>
            <button
              onClick={() => setActionError(null)}
              className="ml-4 rounded p-1 hover:bg-destructive/20"
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}

        <DataGrid<TAdminUser>
          getRowId={(row) => row.id}
          persistKey="admin.settings.admins"
          ariaLabel="Admin accounts"
          data={{
            mode: "client",
            fetcher,
            deps: [reloadTick],
            searchFields: ["first_name", "last_name", "email", "role"],
          }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name, email, or role",
            create: { label: "Add Admin", onClick: () => setIsModalOpen(true) },
          }}
          rowActions={(admin) => [
            {
              label: "View",
              icon: <Eye className="h-4 w-4" />,
              onClick: () => setViewAdminId(admin.id),
            },
            {
              label:
                admin.id === currentUser?.id ? "Revoke admin role (you)" : "Revoke admin role",
              icon: <UserMinus className="h-4 w-4" />,
              variant: "destructive",
              // Self-lockout guard: an admin cannot revoke their own role.
              disabled: admin.id === currentUser?.id,
              onClick: () => setConfirmDialog({ open: true, admin }),
            },
          ]}
          emptyState={{
            title: "No admin accounts found.",
            description: "Add an admin to grant elevated access.",
            action: { label: "Add Admin", onClick: () => setIsModalOpen(true) },
          }}
        />

        {/* Add Admin Modal */}
        <AddAdminModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onSuccess={reload}
        />

        {/* Admin Detail Modal (read-only) */}
        <UserDetailModal
          open={viewAdminId !== null}
          onOpenChange={(open) => {
            if (!open) setViewAdminId(null);
          }}
          userId={viewAdminId}
          readOnly
          title="Admin Details"
          description="View this admin's profile and assigned roles"
        />

        {/* Revoke Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => {
            if (!open) setConfirmDialog({ open: false, admin: null });
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke Admin Role</DialogTitle>
              <DialogDescription>
                {confirmDialog.admin
                  ? `Are you sure you want to revoke the admin role from ${confirmDialog.admin.first_name} ${confirmDialog.admin.last_name}? They will lose access to the admin panel, but their account will not be deleted.`
                  : "Are you sure you want to revoke this admin role?"}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, admin: null })}
                disabled={isRevoking}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  void handleRevokeConfirm();
                }}
                disabled={isRevoking}
              >
                {isRevoking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Revoking…
                  </>
                ) : (
                  "Revoke role"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
